import { StandardEvents } from '@shopify/events';

export class PhoneModelGuard {
  constructor(marker) {
    this.marker = marker;
    this.root = marker.closest('.shopify-section');
    this.productId = marker.dataset.productId;
    this.choice = '';
    this.confirmedVariant = '';
    this.pending = false;
    this.sequence = 0;
    this.abort = new AbortController();
    if (!this.root || !this.productId) return;
    const options = { signal: this.abort.signal };
    this.root.addEventListener('change', (event) => this.onChange(event), { ...options, capture: true });
    this.root.addEventListener('submit', (event) => this.onSubmit(event), { ...options, capture: true });
    this.root.addEventListener('click', (event) => this.onClick(event), { ...options, capture: true });
    this.root.addEventListener(StandardEvents.productSelect, (event) => this.onProductSelect(event), options);
    this.observer = new MutationObserver(() => this.reconcile());
    this.observer.observe(this.root, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'value', 'data-product-id'] });
    this.reset();
  }

  get picker() {
    return [...this.root.querySelectorAll('variant-picker')].find((node) => node.dataset.productId === this.productId);
  }

  get select() {
    return [...(this.picker?.querySelectorAll('select') ?? [])].find((select) => [...select.options].some((option) => /^\s*iPhone\s/i.test(option.value)));
  }

  get formComponent() {
    return [...this.root.querySelectorAll('product-form-component')].find((node) => node.dataset.productId === this.productId);
  }

  get buttons() {
    return [...(this.formComponent?.querySelectorAll('[ref="addToCartButton"]') ?? [])];
  }

  get currentVariantId() {
    return this.formComponent?.querySelector('input[name="id"]')?.value ?? '';
  }

  get ready() {
    return Boolean(this.choice && !this.pending && this.confirmedVariant && this.select?.value === this.choice && this.currentVariantId === this.confirmedVariant);
  }

  message(key, model = '') {
    const node = this.marker.querySelector('[data-pc-model-status]');
    const text = (this.marker.dataset[key] ?? '').replace('[model]', model);
    if (node && node.textContent !== text) node.textContent = text;
  }

  lock() {
    for (const button of this.buttons) {
      if (!button.disabled) button.disabled = true;
      button.setAttribute('aria-describedby', this.marker.querySelector('[data-pc-model-status]')?.id ?? '');
    }
  }

  reset(message = 'chooseMessage') {
    clearTimeout(this.pendingTimer);
    this.sequence += 1;
    this.choice = '';
    this.confirmedVariant = '';
    this.pending = false;
    this.reconcile();
    this.message(message);
  }

  reconcile() {
    if (!this.marker.isConnected || !this.root) return;
    if (this.marker.dataset.productId !== this.productId) {
      this.productId = this.marker.dataset.productId;
      this.reset();
      return;
    }
    if (this.confirmedVariant && this.currentVariantId !== this.confirmedVariant) {
      this.reset('errorMessage');
      return;
    }
    const select = this.select;
    if (!select) {
      this.lock();
      this.message('missingMessage');
      return;
    }
    let placeholder = select.querySelector('[data-pc-model-placeholder]');
    if (!placeholder) {
      placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.marker.dataset.placeholder;
      placeholder.dataset.pcModelPlaceholder = '';
      placeholder.disabled = true;
      select.prepend(placeholder);
    }
    if (!select.required) select.required = true;
    select.setAttribute('aria-describedby', this.marker.querySelector('[data-pc-model-status]').id);
    if (!this.choice) {
      if (select.value !== '') select.value = '';
      for (const option of select.options) {
        if (option !== placeholder && option.hasAttribute('selected')) option.removeAttribute('selected');
      }
      if (!placeholder.hasAttribute('selected')) placeholder.setAttribute('selected', 'selected');
    } else {
      const chosen = [...select.options].find((option) => option.value === this.choice);
      if (!chosen) {
        this.reset('chooseMessage');
        return;
      }
      if (select.value !== this.choice) select.value = this.choice;
      for (const option of select.options) {
        if (option !== chosen && option.hasAttribute('selected')) option.removeAttribute('selected');
      }
      if (!chosen.hasAttribute('selected')) chosen.setAttribute('selected', 'selected');
    }
    if (!this.ready) this.lock();
  }

  watchPending() {
    clearTimeout(this.pendingTimer);
    const sequence = this.sequence;
    this.pendingTimer = setTimeout(() => {
      if (this.pending && sequence === this.sequence) this.reset('errorMessage');
    }, 15000);
  }

  onChange(event) {
    const picker = event.target?.closest?.('variant-picker');
    if (!picker || picker !== this.picker) return;
    const select = this.select;
    if (event.target === select) {
      const option = select.selectedOptions[0];
      if (!option?.dataset.optionValueId || !/^\s*iPhone\s/i.test(option.value)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.reset();
        return;
      }
      this.sequence += 1;
      this.choice = option.value;
      this.confirmedVariant = '';
      this.pending = true;
      this.watchPending();
      this.lock();
      this.message('loadingMessage');
    } else if (!this.choice) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.lock();
      this.message('chooseMessage');
      select?.focus();
    } else {
      this.sequence += 1;
      this.confirmedVariant = '';
      this.pending = true;
      this.watchPending();
      this.lock();
      this.message('loadingMessage');
    }
  }

  onProductSelect(event) {
    if (event.target?.closest?.('variant-picker') !== this.picker) return;
    const sequence = this.sequence;
    const choice = this.choice;
    this.confirmedVariant = '';
    this.pending = true;
    this.watchPending();
    this.lock();
    if (!event.promise || !choice) {
      this.reset();
      return;
    }
    event.promise.then(({ detail }) => {
      queueMicrotask(() => {
        if (!this.marker.isConnected || sequence !== this.sequence || choice !== this.choice) return;
        clearTimeout(this.pendingTimer);
        const resource = detail?.resource;
        const sameProduct = String(detail?.productId ?? '') === String(this.productId);
        const exactModel = resource?.options?.includes(choice);
        const variantId = resource?.id == null ? '' : String(resource.id);
        this.pending = false;
        this.reconcile();
        if (!sameProduct || !exactModel || !variantId || this.currentVariantId !== variantId) {
          this.reset('errorMessage');
          return;
        }
        if (resource.available !== true) {
          this.confirmedVariant = '';
          this.lock();
          this.message('unavailableMessage');
          return;
        }
        this.confirmedVariant = variantId;
        this.reconcile();
        if (this.ready) {
          for (const button of this.buttons) button.disabled = false;
          this.message('readyMessage', choice);
        }
      });
    }).catch(() => {
      if (sequence === this.sequence && this.marker.isConnected) this.reset('errorMessage');
    });
  }

  preventPurchase(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.lock();
    this.message(this.pending ? 'loadingMessage' : 'chooseMessage');
    this.select?.focus();
  }

  onSubmit(event) {
    if (event.target?.closest?.('product-form-component') === this.formComponent && !this.ready) this.preventPurchase(event);
  }

  onClick(event) {
    const button = event.target?.closest?.('[ref="addToCartButton"]');
    if (button && this.buttons.includes(button) && !this.ready) this.preventPurchase(event);
  }

  destroy() {
    clearTimeout(this.pendingTimer);
    this.abort.abort();
    this.observer?.disconnect();
  }
}

const guards = new Map();
export function installModelGuards() {
  for (const [marker, guard] of guards) {
    if (!marker.isConnected) {
      guard.destroy();
      guards.delete(marker);
    }
  }
  for (const marker of document.querySelectorAll('[data-pc-model-guard]')) {
    if (!guards.has(marker) && marker.dataset.productId && marker.closest('.shopify-section')) guards.set(marker, new PhoneModelGuard(marker));
  }
}

installModelGuards();
new MutationObserver(installModelGuards).observe(document.body, { childList: true, subtree: true });
window.addEventListener('pageshow', (event) => {
  installModelGuards();
  if (event.persisted) for (const guard of guards.values()) guard.reset();
});
window.addEventListener('popstate', () => {
  for (const guard of guards.values()) guard.reset();
});
document.addEventListener('shopify:section:load', installModelGuards);
